import { useMemo, useState } from "react";
import {
  Column,
  DataGrid,
  FilterRow,
  HeaderFilter,
  LoadPanel,
  Paging,
  Selection,
  Scrolling,
} from "devextreme-react/data-grid";
import { Pager } from "devextreme-react/tree-list";
import { TagBox } from "devextreme-react/tag-box";
import { SelectBox } from "devextreme-react/select-box";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import { dataGridConfig } from "../../constants";
import { Entity, UserPerSite } from "../../api-client-nswag/custompermission-client";
import { applyOverflowTooltipOnCellPrepared, renderTruncatedTextCell } from "./manageCustomPermissionsGridHelpers";

export type BulkEntityUsersMap = Record<number, string[]>;

interface BulkUserAssignmentManagerProps {
  selectedEntities: Entity[];
  allEntities: Entity[];
  onSelectedEntityIdsChange: (nextSelectedIds: Array<string | number>) => void;
  allUsers: UserPerSite[];
  entityUsersMap: BulkEntityUsersMap;
  setEntityUsersMap: React.Dispatch<React.SetStateAction<BulkEntityUsersMap>>;
}

type Row = {
  userId: string;
  username: string;
  userEmail: string;
  userGroup: string;
  userRole?: string;
};

const BulkUserAssignmentManager: React.FC<BulkUserAssignmentManagerProps> = ({
  selectedEntities,
  allEntities,
  onSelectedEntityIdsChange,
  allUsers,
  entityUsersMap,
  setEntityUsersMap,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string | number | null>(null);

  const allUsersIndex = useMemo(() => {
    const map = new Map<string, UserPerSite>();
    (allUsers ?? []).forEach((u) => map.set(String(u.userId), u));
    return map;
  }, [allUsers]);

  const selectedEntityIds = useMemo(
    () =>
      (selectedEntities ?? [])
        .map((e: any) => Number(e?.entityId))
        .filter((id) => Number.isFinite(id) && id !== 0),
    [selectedEntities]
  );

  const selectedEntityIdsNorm = useMemo(() => {
    const ids = [...selectedEntityIds].sort((a, b) => a - b);
    return ids.join(",");
  }, [selectedEntityIds]);

  const allEntitiesList = useMemo(() => {
    return (allEntities ?? []).filter((e: any) => {
      const id = Number(e?.entityId);
      return Number.isFinite(id) && id !== 0;
    });
  }, [allEntities]);

  const entitiesLabel = (entity: Entity) => `${entity.entityId}-${entity.entityName}`;

  const isUserInAllSelectedEntities = (userId: string): boolean => {
    for (const entityId of selectedEntityIds) {
      const usersForEntity = entityUsersMap[entityId] ?? [];
      if (!usersForEntity.includes(userId)) return false;
    }
    return selectedEntityIds.length > 0;
  };

  const availableUsersToAdd = useMemo(() => {
    const usersList = allUsers ?? [];
    if (selectedEntityIds.length === 0) return usersList;

    return usersList.filter((u) => !isUserInAllSelectedEntities(String(u.userId)));
  }, [allUsers, entityUsersMap, selectedEntityIds.join(",")]);

  const addUserToAllEntities = (userIdRaw: string | number | null | undefined) => {
    if (userIdRaw === null || userIdRaw === undefined) return;
    if (selectedEntityIds.length === 0) {
      setSelectedUserId(null);
      return;
    }
    const userId = String(userIdRaw);

    setEntityUsersMap((prev) => {
      const next: BulkEntityUsersMap = { ...prev };
      for (const entityId of selectedEntityIds) {
        const current = next[entityId] ?? [];
        if (current.includes(userId)) continue;
        next[entityId] = [...current, userId];
      }
      return next;
    });

    setSelectedUserId(null);
  };

  const removeUserFromAllEntities = (userId: string) => {
    setEntityUsersMap((prev) => {
      const next: BulkEntityUsersMap = { ...prev };
      for (let i = 0; i < selectedEntityIds.length; i++) {
        const entityId = selectedEntityIds[i];
        const current = next[entityId] ?? [];
        next[entityId] = current.filter((id) => id !== userId);
      }
      return next;
    });
  };

  const rows: Row[] = useMemo(() => {
    const unionUserIds = new Set<string>();

    for (let i = 0; i < selectedEntityIds.length; i++) {
      const entityId = selectedEntityIds[i];
      const userIds = entityUsersMap[entityId] ?? [];
      for (let j = 0; j < userIds.length; j++) {
        unionUserIds.add(String(userIds[j]));
      }
    }

    const result: Row[] = [];
    const ids = Array.from(unionUserIds);
    for (let i = 0; i < ids.length; i++) {
      const userId = ids[i];
      const user = allUsersIndex.get(String(userId));
      if (!user) continue;

      result.push({
        userId: String(userId),
        username: user.username ?? "",
        userEmail: user.userEmail ?? "",
        userGroup: user.userGroup ?? "",
        userRole: user.userRole ?? "",
      });
    }

    return result;
  }, [allUsersIndex, entityUsersMap, selectedEntityIds]);

  const cellRenderUserName = (cellData: any) => {
    return (
      <span className="hyper-link-blue">
        {cellData?.data?.username}
      </span>
    );
  };

  const cellRenderEmail = (cellData: any) => {
    return renderTruncatedTextCell(cellData?.data?.userEmail);
  };

  const handleDataGridCellPrepared = (e: any) => {
    applyOverflowTooltipOnCellPrepared(e);
  };

  const cellRenderActions = (e: any) => {
    const rowData = e.data as Row;

    const groupName = (rowData?.userGroup ?? "").toLowerCase();
    const isProtected = ["content admin", "site admin"].some((role) =>
      groupName.includes(role)
    );

    return (
      <RemoveCircleOutlineIcon
        fontSize="small"
        className={isProtected ? "mcp-action-icon mcp-action-icon--disabled" : "mcp-action-icon"}
        onClick={isProtected ? undefined : () => removeUserFromAllEntities(rowData.userId)}
      />
    );
  };

  const allEntitiesIndex = useMemo(() => {
    const map = new Map<number, Entity>();
    (allEntitiesList ?? []).forEach((e: any) => {
      const id = Number(e?.entityId);
      if (!Number.isFinite(id) || id === 0) return;
      map.set(id, e as Entity);
    });
    return map;
  }, [allEntitiesList]);

  const removeEntityFromSelection = (entityId: number) => {
    if (!Number.isFinite(entityId) || entityId === 0) return;
    const next = (selectedEntityIds ?? []).filter((id) => Number(id) !== Number(entityId));
    onSelectedEntityIdsChange(next);
  };

  const renderEntityTag = (tagData: any) => {
    const entityId = Number(tagData?.entityId ?? tagData?.value ?? tagData);
    const index = selectedEntityIds.indexOf(entityId);
    const hiddenCount = Math.max(0, selectedEntityIds.length - 3);

    if (index === 3 && hiddenCount > 0) {
      return <span className="entity-tag-more">+{hiddenCount} More</span>;
    }

    if (index >= 0 && index < 3) {
      const entity = allEntitiesIndex.get(entityId) ?? (tagData as Entity);
      const label = entity ? entitiesLabel(entity) : String(entityId);

      return (
        <span className="entity-tag">
          <span className="entity-tag__label">{label}</span>
          <button
            type="button"
            className="entity-tag__remove"
            aria-label={`Remove ${label}`}
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              removeEntityFromSelection(entityId);
            }}
          >
            ×
          </button>
        </span>
      );
    }

    const entity = allEntitiesIndex.get(entityId) ?? (tagData as Entity);
    const label = entity ? entitiesLabel(entity) : String(entityId);
    return <span className="entity-tag-hidden" aria-label={label} />;
  };

  return (
    <div className="section-wrap bulk-user-assignment">
      <div className="block">
        <span className="block-title">Legal Entities</span>
        <span className="block-subtitle">The following legal entities have been selected:</span>
        <div className="entity-picker">
          <TagBox
            dataSource={allEntitiesList}
            value={selectedEntityIds}
            valueExpr="entityId"
            displayExpr={(e: any) => (e ? entitiesLabel(e as Entity) : "")}
            placeholder="Search"
            searchEnabled={true}
            searchMode="contains"
            showSelectionControls={true}
            showClearButton={false}
            showMultiTagOnly={false}
            applyValueMode="instantly"
            multiline={false}
            tagRender={renderEntityTag}
            onValueChanged={(e: any) => {
              const next = (e?.value ?? []) as Array<string | number>;
              const nextNorm = (next ?? [])
                .map((k) => Number(k))
                .filter((id) => Number.isFinite(id) && id !== 0)
                .sort((a, b) => a - b)
                .join(",");

              if (nextNorm === selectedEntityIdsNorm) return;

              onSelectedEntityIdsChange(next);
              setSelectedUserId(null);
            }}
            dropDownButtonRender={() => {
              return (
                <div className="custom-dropdown-btn">
                  <span className="dx-icon dx-icon-chevrondown" />
                </div>
              );
            }}
            width="100%"
          />
        </div>
      </div>

      <div className="block">
        <span className="block-title">Users</span>
        <span className="block-subtitle">Select which users you would like to add to the selected legal entities:</span>

        <div className="control-row">
          <SelectBox
            dataSource={availableUsersToAdd}
            valueExpr="userId"
            displayExpr="username"
            value={selectedUserId}
            placeholder="Enter a name or select from the dropdown list"
            showClearButton={true}
            searchEnabled={true}
            searchMode="contains"
            onSelectionChanged={({ selectedItem }) => {
              if (!selectedItem) return;
              addUserToAllEntities(selectedItem.userId);
            }}
            dropDownButtonRender={() => {
              return (
                <div className="custom-dropdown-btn">
                  <span className="dx-icon dx-icon-chevrondown" />
                </div>
              );
            }}
            width="100%"
          />
        </div>

        <div className="grid-wrap">
          <DataGrid
            height={300}
            {...dataGridConfig}
            dataSource={rows}
            noDataText={"Add Users to the selected entities using the field above."}
            allowColumnResizing={true}
            showBorders={true}
            showColumnLines={false}
            showRowLines={true}
            keyExpr="userId"
            onCellPrepared={handleDataGridCellPrepared}
          >
            <Pager visible={false} />
            <Paging enabled={false} />
            <Scrolling mode="virtual" />
            <LoadPanel enabled={false} />
            <FilterRow visible={true} />
            <HeaderFilter visible={true} />
            <Selection mode="single" />

            <Column alignment="left" dataField="username" caption="Name" cellRender={cellRenderUserName} />
            <Column alignment="left" dataField="userRole" caption="Group" />
            <Column cssClass="cell-text" alignment="left" dataField="userEmail" caption="Email ID" cellRender={cellRenderEmail} />
            <Column type="buttons" caption="Actions" width={90} cellRender={cellRenderActions} />
          </DataGrid>
        </div>
      </div>
    </div>
  );
};

export default BulkUserAssignmentManager;
